import type { Shell, ShellId } from './types';
import { PipHome } from '../ui/shells/kids-pal/Home';
import { TilesHome } from '../ui/shells/kids-tiles/Home';
import { CrewHome } from '../ui/shells/teens-crew/Home';
import { VibesHome } from '../ui/shells/teens-vibes/Home';
import { DeckHome } from '../ui/shells/teens-deck/Home';

export class ShellRegistry {
  #shells = new Map<ShellId, Shell>();

  register(shell: Shell): void {
    if (this.#shells.has(shell.id)) {
      throw new Error(`shell already registered: ${shell.id}`);
    }
    this.#shells.set(shell.id, shell);
  }

  get(id: ShellId): Shell | undefined {
    return this.#shells.get(id);
  }

  has(id: ShellId): boolean {
    return this.#shells.has(id);
  }

  all(): Shell[] {
    return Array.from(this.#shells.values());
  }

  clear(): void {
    this.#shells.clear();
  }
}

export const SHELL_REGISTRY = new ShellRegistry();

export const DEFAULT_SHELL_ID = 'adult-hub';

/**
 * Adult Hub — the no-op default shell. No Home swap (falls through to
 * the existing hub/SurfaceStack landing), no kid lock, no mascot
 * persona, base theme tokens only.
 *
 * Foundation ships with just this shell registered. Subsequent ships
 * (v11.g–k) add Kids/Teens shells. Future ships add more adult shells.
 */
export const ADULT_HUB_SHELL: Shell = {
  id: DEFAULT_SHELL_ID,
  persona: 'adult',
  label: 'Hub',
  blurb: 'The default ALDS shell. Hierarchical hub, full surface mesh, technical inspector visible.',
};

/**
 * Phase 73 full · v11.g — Kids C · Helper Pal (Pip).
 *
 * Pip the fox greets the user and offers four conversational big
 * buttons that bridge into existing surfaces. The shell also sets a
 * mascot persona which the Phase 71 sigil reads from to render Pip's
 * face in place of the abstract ◌/◉/⚠/◎ defaults.
 *
 * kidLock is true: switching INTO Pip enables Phase 71 kid-mode (PIN
 * required); switching OUT requires the same PIN. See the design doc
 * for the interim coupling.
 */
export const KIDS_PAL_SHELL: Shell = {
  id: 'kids-pal',
  persona: 'kids',
  label: 'Helper Pal · Pip',
  blurb: 'Pip the fox guides every screen with calm, conversational buttons. Best for ages 7-10.',
  ageRange: [7, 10],
  Home: PipHome,
  mascot: {
    id: 'Pip',
    glyph: '🦊',
    expressions: {
      idle: '🦊',
      watching: '👀',
      justFlagged: '😟',
      summoned: '✨',
    },
  },
  kidLock: true,
  themeImporter: () => import('../ui/shells/kids-pal/theme.css'),
};

/**
 * Phase 73 full · v11.h — Kids A · Helper Tiles.
 *
 * 2-column big-tile grid + SOS bar. No mascot persona; lower visual
 * overhead than Pip. Same kidLock semantics — switching in/out
 * coupled to Phase 71 kid-mode PIN.
 */
export const KIDS_TILES_SHELL: Shell = {
  id: 'kids-tiles',
  persona: 'kids',
  label: 'Helper Tiles',
  blurb: 'Big chunky tiles — tablet-home-screen feel. Best for ages 7-10 who prefer a grid.',
  ageRange: [7, 10],
  Home: TilesHome,
  kidLock: true,
  themeImporter: () => import('../ui/shells/kids-tiles/theme.css'),
};

/**
 * Phase 73 full · v11.i — Teens C · Pocket Crew.
 *
 * Messaging-first home with a tab strip including a first-class
 * "Helping" tab that bridges to env-board (civic mutual aid as a
 * native destination, not a hidden sub-feature). Muted sage + clay
 * palette. No kidLock — teens are old enough to switch shells freely.
 */
export const TEENS_CREW_SHELL: Shell = {
  id: 'teens-crew',
  persona: 'teens',
  label: 'Pocket Crew',
  blurb: 'Messaging-first, muted palette, with a first-class Helping tab. Best for 13-18.',
  ageRange: [13, 18],
  Home: CrewHome,
  themeImporter: () => import('../ui/shells/teens-crew/theme.css'),
};

/**
 * Phase 73 full · v11.j — Teens A · Vibes.
 *
 * Dark-mode social-first home with a stories carousel, drop button,
 * and friends grid. Higher visual energy than Pocket Crew. Stories
 * map to bulletins with a 24h TTL; "Drop something" bridges to
 * social-compose; SOS bridges to env-intake. No kidLock.
 */
export const TEENS_VIBES_SHELL: Shell = {
  id: 'teens-vibes',
  persona: 'teens',
  label: 'Vibes',
  blurb: 'Dark-mode social-first with stories carousel + expressive composer. Best for 13-18.',
  ageRange: [13, 18],
  Home: VibesHome,
  themeImporter: () => import('../ui/shells/teens-vibes/theme.css'),
};

/**
 * Phase 73 full · v11.k — Teens B · Command Deck.
 *
 * Linear/Notion power-user shell. ⌘K (or Ctrl+K) opens the Phase 73
 * interim sass-surface-map drawer as a command palette. Four stat
 * cards (UNREAD/JOBS/FRIENDS/VOTES) act as one-tap shortcuts. Lays
 * groundwork for a future power-user adult shell.
 */
export const TEENS_DECK_SHELL: Shell = {
  id: 'teens-deck',
  persona: 'teens',
  label: 'Command Deck',
  blurb: 'Power-user dark grey shell with ⌘K palette + stat cards. Best for 13-18 who want control.',
  ageRange: [13, 18],
  Home: DeckHome,
  themeImporter: () => import('../ui/shells/teens-deck/theme.css'),
};

export function registerBuiltInShells(reg: ShellRegistry = SHELL_REGISTRY): void {
  if (!reg.has(ADULT_HUB_SHELL.id)) reg.register(ADULT_HUB_SHELL);
  if (!reg.has(KIDS_PAL_SHELL.id)) reg.register(KIDS_PAL_SHELL);
  if (!reg.has(KIDS_TILES_SHELL.id)) reg.register(KIDS_TILES_SHELL);
  if (!reg.has(TEENS_CREW_SHELL.id)) reg.register(TEENS_CREW_SHELL);
  if (!reg.has(TEENS_VIBES_SHELL.id)) reg.register(TEENS_VIBES_SHELL);
  if (!reg.has(TEENS_DECK_SHELL.id)) reg.register(TEENS_DECK_SHELL);
}
